import React, { useState, useEffect } from "react";
import apiHelper from "../../../../util/ApiHelper";
import {
  usePagination,
  useRowSelect,
  useTable,
  useGlobalFilter,
} from "react-table";
import {
  IconButton,
  FormControl,
  InputLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import { Delete, Edit } from "@material-ui/icons";
import styled from "styled-components";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { CustomCheckbox, modal } from "../../../../util";
import { Link, Link as RouterLink, useRouteMatch } from "react-router-dom";
import { DatePicker } from "@material-ui/pickers";

const useStylesAll = makeStyles({
  table: {
    border: "1px solid #cccccc",
    borderCollapse: "initial",
    borderRadius: "5px",
    overflow: "hidden",
    padding: 0,
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  iconBtn: {
    color: "#CCCCCC",
    display: "inline-block",
    width: "20%",
    margin: 5,
  },
  image: {
    maxWidth: "80px",
  },
  statusActive: {
    backgroundColor: "#306F9D",
    borderRadius: "4px",
    padding: "3px 10px",
    color: "#FFFFFF",
  },
  statusInactive: {
    backgroundColor: "#CCCCCC",
    borderRadius: "4px",
    padding: "3px 10px",
    color: "#FFFFFF",
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
});

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;
  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;
      &.active {
        font-weight: bold;
        color: #333333;
      }
    }
    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }
    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const statusMap = {
  0: "inactive",
  1: "active",
};

export default function BannerTable(props) {
  const {
    activeTab,
    globalFilter,
    queryString,
    setQueryString,
    queryDate,
    setQueryDate,
    data,
    setData,
    setOnChange,
    disabled,
    getBanners,
    setParentPageIndex,
    setParentPageSize,
    elementCounts,
    totalPages,
    status,
    setStatus,
  } = props;
  const [actionType, setActionType] = React.useState("");
  const [selectedDate, handleDateChange] = React.useState(null);
  const [keyword, setKeyword] = useState("");
  const match = useRouteMatch();

  const formatDatetime = (dt) => {
    return dayjs(dt).format("DD MMM YYYY, hh:mma");
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ row }) => {
          return (
            row.original.id
          );
        },
      },
      {
        Header: "Image",
        accessor: "localizations.en.image",
        Cell: ({ row }) => {
          console.log(row);
          return (
            <img
              src={
                row.original.localizations &&
                row.original.localizations.en &&
                row.original.localizations.en.image
              }
              className={classes.image}
              alt=""
            />
          );
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Name",
        accessor: "localizations.en.title",
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations &&
                row.original.localizations.en &&
                row.original.localizations.en.title}
            </Link>
          );
        },
      },
      {
        Header: "Start Time",
        accessor: "startDatetime",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return <span>{formatDatetime(row.original.startDatetime)}</span>;
        },
      },
      {
        Header: "End Time",
        accessor: "endDatetime",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return <span>{formatDatetime(row.original.endDatetime)}</span>;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          if (row.original.status === 1) {
            return <span className={classes.statusActive}>Active</span>;
          }
          if (row.original.status === 0) {
            return <span className={classes.statusInactive}>Inactive</span>;
          }
          return "";
        },
        disableGlobalFilter: true,
      },
    ],
    []
  );

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const triggerSearch = React.useCallback(() => {
    setQueryString(keyword);
  }, [keyword, setQueryString]);

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    state: { pageIndex, pageSize },
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      autoResetPage: false,
      pageCount: totalPages,
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...getToggleAllRowsSelectedProps(), disabled }}
              />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...row.getToggleRowSelectedProps(), disabled }}
              />
            </div>
          ),
        },

        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
              {disabled || (
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  // component={CustomLink}
                  onClick={() => {
                    handleDelete(row.original.id);
                  }}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              )}
            </div>
          ),
        },
      ]);
    }
  );

  useEffect(() => {
    setGlobalFilter(globalFilter);
  }, [globalFilter, setGlobalFilter]);

  useEffect(() => {
    setParentPageSize(pageSize);
  }, [pageSize, setParentPageSize]);

  useEffect(() => {
    setParentPageIndex(pageIndex);
  }, [pageIndex, setParentPageIndex]);

  // trigger refetch banner as a side effect on search for pagination
  useEffect(() => {
    setOnChange(true);
  }, [queryString, setOnChange]);

  // reset page index and search states as a side effect on tab switching
  useEffect(() => {
    gotoPage(0);
    setKeyword("");
    setQueryString("");
  }, [activeTab, gotoPage, setQueryString]);

  // reset page index as a side effect on status switching
  useEffect(() => {
    gotoPage(0);
  }, [gotoPage, status]);

  const handleDelete = async (id) => {
    const decision = await modal.fire({
      title: "Delete Banner",
      text: "Are you sure to delete this banner?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        await apiHelper.hkiffApp.deleteHkiffAppBannerById(id);
        toast.dark("The banner is deleted.");
        // trigger condition to refetch all banner data for pagination
        setOnChange(true);
        // refetch current page banners
        getBanners();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleSelectChange = (e) => {
    setActionType(e.target.value);
  };

  const handleBulkAction = React.useCallback(async () => {
    switch (actionType) {
      case "DELETE":
        const ids = selectedFlatRows.map((o) => o.original.id);
        if (ids.length > 0) {
          const decision = await modal.fire({
            title: "Delete Banner",
            text: `Are you sure to delete ${ids.length} banner(s)?`,
            showCancelButton: true,
            confirmButtonText: `Delete`,
          });
          if (decision.isConfirmed) {
            try {
              const promises = ids.map((id) =>
                apiHelper.hkiffApp.deleteHkiffAppBannerById(id)
              );
              await Promise.all(promises);
              toast.dark(`${ids.length} banner(s) deleted.`);
              // trigger condition to refetch all banner data for pagination
              setOnChange(true);
              // refetch current page banners
              getBanners();
            } catch (err) {
              console.log(err);
            }
          }
        }
        break;
      default:
        break;
    }
  }, [actionType, selectedFlatRows, setOnChange, getBanners]);

  const classes = useStylesAll();
  console.log("status", status);

  return (
    <>
      <StatusFilter>
        <li>
          <button
            className={status === null ? "active" : ""}
            type="button"
            onClick={() => setStatus(null)}
          >
            <Typography variant="p">All ({elementCounts.all})</Typography>
          </button>
        </li>
        <li>
          <button
            className={status === 1 ? "active" : ""}
            type="button"
            onClick={() => {
              setStatus(1);
            }}
          >
            <Typography variant="p">Active ({elementCounts.active})</Typography>
          </button>
        </li>
        <li>
          <button
            className={status === 0 ? "active" : ""}
            type="button"
            onClick={() => {
              setStatus(0);
            }}
          >
            <Typography variant="p">
              Inactive ({elementCounts.inactive})
            </Typography>
          </button>
        </li>
      </StatusFilter>
      <Grid container spacing={2} justify="space-between">
        <Grid container item xs={12} justify="space-between">
          <Grid container item xs={6}>
            <Box width="100%" component="span" display="flex">
              <Grid container item xs={2}>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel id="demo-simple-select-outlined-label">
                    Bulk Actions
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    value={actionType}
                    onChange={handleSelectChange}
                    label="Bulk Actions"
                    disabled={disabled}
                  >
                    <MenuItem value={"NONE"}>
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={"DELETE"}>Delete</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid container item xs={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBulkAction}
                  disabled={disabled}
                >
                  Apply
                </Button>
              </Grid>
              <Grid container item xs={2}>
                  <DatePicker
                    fullWidth
                    variant="inline"
                    inputVariant="outlined"
                    openTo="year"
                    margin="dense"
                    views={["year", "month"]}
                    label="Year and Month"
                    value={selectedDate}
                    onChange={handleDateChange}
                  />
                </Grid>
                <Grid container item xs={2}>
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setQueryDate(selectedDate);
                    }}
                  >
                    Filter
                  </Button>
              </Grid>
            </Box>
          </Grid>
          <Grid container item xs={6} justify="flex-end">
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                margin="dense"
                component={RouterLink}
                to={`${match.url}/create/banner`}
              >
                Add New Banner
              </Button>
            </Box>
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box width="100%">
              <Table
                className={classes.table}
                aria-label="table of banners"
                component="ul"
                {...getTableProps()}
              >
                <colgroup>
                  <col style={{ width: "5%" }} />id
                  <col style={{ width: "5%" }} />id
                  <col style={{ width: "10%" }} />image
                  <col style={{ width: "30%" }} />name
                  <col style={{ width: "15%" }} />st
                  <col style={{ width: "15%" }} />et
                  <col style={{ width: "10%" }} />status
                  <col style={{ width: "5%" }} />action
                </colgroup>
                <TableHead component="div">
                  {headerGroups.map((headerGroup) => {
                    return (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                          return (
                            <TableCell
                              style={{
                                paddingTop: 16,
                                paddingBottom: 16,
                                verticalAlign: "baseline",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {column.render("Header")}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableHead>
                <TableBody
                        component="div"
                        {...getTableBodyProps()}
                      >
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <TableRow {...row.getRowProps()}>
                              {row.cells.map((cell) => {
                                return (
                                <TableCell {...cell.getCellProps()}
                                style={{
                                  paddingTop: 6,
                                  paddingBottom: 6,
                                }}>
                                  {cell.render("Cell")}
                                </TableCell>
                                )
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
              </Table>
              <Grid
                container
                spacing={12}
                justify="space-between"
                alignItems="center"
              >
                <Grid container item xs={7}></Grid>
                <Grid container item xs={5} justify="flex-end">
                  <div className={classes.pagination}>
                    Show rows:{" "}
                    <TextField
                      id="page-rows"
                      select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                      }}
                      variant="outlined"
                      style={{ minWidth: "70px" }}
                    >
                      {[5, 10, 25].map((pageSize) => (
                        <MenuItem key={pageSize} value={pageSize}>
                          {pageSize}
                        </MenuItem>
                      ))}
                    </TextField>
                    Go to{" "}
                    <TextField
                      id="pagination-field"
                      variant="outlined"
                      onChange={(e) => {
                        const page = e.target.value
                          ? Number(e.target.value) - 1
                          : 0;
                        gotoPage(page);
                      }}
                    />
                    {pageIndex * pageSize + 1} to{" "}
                    {(pageIndex + 1) * pageSize >
                    elementCounts[statusMap[status] || "all"]
                      ? elementCounts[statusMap[status] || "all"]
                      : (pageIndex + 1) * pageSize}{" "}
                    of {elementCounts[statusMap[status] || "all"]}
                    <Button
                      onClick={previousPage}
                      disabled={!canPreviousPage}
                      className="left"
                    >
                      {"<"}
                    </Button>
                    <Button
                      onClick={nextPage}
                      disabled={!canNextPage}
                      className="right"
                    >
                      {">"}
                    </Button>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                justify="flex-end"
                style={{ paddingRight: "25px", marginTop: "15px" }}
              >
                <div className={classes.flexRow}>
                  <div></div>
                </div>
              </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
